import { trpc, trpcApi } from '@bitbot/api';
import { createGlobalStore } from 'hox';
import { useWallets } from './list';
import { useEffect, useState, useCallback } from 'react';
import { keyBy, debounce } from 'lodash';
import { ethers } from 'ethers';
import { Notify } from '@bitbot/ui-design';
import { useRouter } from 'next/router';
import { useWalletChangeParams } from './common';
import { useToken } from '../user/token';

type config = { tokenAddress: string; isShow: boolean };

//更改token
export const [useManageAsset] = createGlobalStore(() => {
  const { hasLogin } = useToken();
  const { currentWallet, baseAssetConfig, setBaseAssetConfig } =
    useWallets();
  const resetOrGetShowCofig =
    trpc.wallet.resetOrGetWalletTokenShowConfig.useMutation();
  const setConfigMutation =
    trpc.wallet.setWalletTokenShowConfig.useMutation();
  const [currentConfig, setCurrentConfig] = useState<config[]>([]);

  const { setAllParams, setShowParams } = useWalletChangeParams();

  const _setCurrentConfig = (configs: config[]) => {
    setBaseAssetConfig({
      ...baseAssetConfig,
      [currentWallet!]: [...configs],
    });
    setCurrentConfig(configs);
  };

  useEffect(() => {
    if (currentWallet && hasLogin) {
      //读取本地数据,与后台的备份进行merge
      let cache: config[] = baseAssetConfig![currentWallet] ?? [];
      if (cache.length === 0) {
        cache.push({
          tokenAddress: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
          isShow: true,
        });
        cache.push({
          tokenAddress: '0x252D223D0550BC6c137B003D90bc74F5341A2818',
          isShow: true,
        });
      }
      let obj = keyBy(cache, 'tokenAddress');
      if (!('0x252D223D0550BC6c137B003D90bc74F5341A2818' in obj)) {
        cache.push({
          tokenAddress: '0x252D223D0550BC6c137B003D90bc74F5341A2818',
          isShow: true,
        });
      }
      if (cache)
        resetOrGetShowCofig
          .mutateAsync({ wallet: currentWallet })
          .then(data => {
            //合并后端与前端的config信息
            for (let config of data) {
              const find = cache.find(
                item => item.tokenAddress === config.tokenAddress,
              );
              if (!find) {
                cache.push({ ...config });
              }
            }
            _setCurrentConfig(cache);
            setAllParams({
              address: currentWallet,
              tokenAddresses: [...cache.map(item => item.tokenAddress)],
            });
            setShowParams({
              address: currentWallet,
              tokenAddresses: [
                ...cache
                  .filter(item => item.isShow)
                  .map(item => item.tokenAddress),
              ],
            });
          });
    }
  }, [currentWallet, hasLogin]);

  const updateWalletConfig = async (address: string, isShow: boolean) => {
    if (currentWallet) {
      //更改后端备份
      setConfigMutation.mutateAsync({
        wallet: currentWallet,
        tokens: [
          {
            address,
            isShow,
          },
        ],
      });
      //更新前端备份
      const config = currentConfig.find(
        config => config.tokenAddress === address,
      );
      if (config) {
        config.isShow = isShow;
      } else {
        currentConfig.push({
          tokenAddress: address,
          isShow,
        });
      }
      _setCurrentConfig(currentConfig);
      //重新发起请求获取所有isShow为true的余额
      setShowParams({
        address: currentWallet,
        tokenAddresses: currentConfig
          .filter(item => item.isShow)
          .map(item => item.tokenAddress),
      });
    }
  };

  return {
    updateWalletConfig,
    currentConfig,
    currentConfigObj: keyBy(currentConfig, 'tokenAddress'),
    setCurrentConfig: _setCurrentConfig,
  };
});

//添加token
export const useCustomAsset = () => {
  const [tokenInfo, setTokenInfo] = useState<{
    name: string;
    symbol: string;
    decimals: number;
  }>();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [address, setAddress] = useState('');
  const { setAllParams, setShowParams } = useWalletChangeParams();
  const router = useRouter();
  const {
    baseAssetConfig,
    walletAssetConfig,
    setBaseAssetConfig,
    walletAssetConfigObj,
    currentWallet,
  } = useWallets();

  const customAsset = () => {
    trpcApi.wallet.setWalletTokenShowConfig
      .mutate({
        wallet: currentWallet!,
        tokens: [
          {
            address: address,
            isShow: true,
            name: tokenInfo?.name,
            decimals: tokenInfo?.decimals,
            symbol: tokenInfo?.symbol,
          },
        ],
      })
      .then(() => {
        //更新本地缓存的asset config数据
        walletAssetConfig.push({ tokenAddress: address, isShow: true }); //新增
        setBaseAssetConfig({
          ...baseAssetConfig,
          [currentWallet!]: [...walletAssetConfig],
        });
        //还要触发重新调用两次getWalletBalance接口（获取全部数据和isShow为true的数据）
        setAllParams({
          address: currentWallet!,
          tokenAddresses: [
            ...walletAssetConfig.map(item => item.tokenAddress),
          ],
        });
        setShowParams({
          address: currentWallet!,
          tokenAddresses: [
            ...walletAssetConfig
              .filter(item => item.isShow)
              .map(item => item.tokenAddress),
          ],
        });
        //退回上一个页面
        router.back();
      })
      .catch(() => {
        Notify.error('Import token failed, please try again later');
      });
  };

  const getTokenInfo = useCallback(
    debounce((token: string) => {
      if (ethers.utils.isAddress(token)) {
        if (walletAssetConfigObj[token]) {
          setError('Token has been added');
          return;
        }
        setIsLoading(true);
        trpcApi.market.getTokenInfo
          .query({ token, chain: 'base' })
          .then(data => {
            // @ts-ignore
            setTokenInfo({ name: '', symbol: '', decimals: 18, ...data });
            setError('');
          })
          .catch(error => {
            setError(error.message);
            setTokenInfo(undefined);
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        setTokenInfo(undefined);
        if (token === '') {
          setError('');
        } else {
          setError('Please enter a valid address');
        }
      }
    }, 500),
    [walletAssetConfigObj],
  );

  return {
    tokenInfo,
    getTokenInfo,
    error,
    isLoading,
    address,
    setAddress,
    customAsset,
  };
};

export const usecustomAssetWithParams = () => {
  const {
    baseAssetConfig,
    walletAssetConfig,
    setBaseAssetConfig,
    currentWallet,
  } = useWallets();
  const { setAllParams, setShowParams } = useWalletChangeParams();
  const customAssetWithParams = (
    tokens: {
      address: string;
      isShow: boolean;
      name?: string;
      symbol?: string;
      decimals?: number;
    }[],
  ) => {
    trpcApi.wallet.setWalletTokenShowConfig
      .mutate({
        wallet: currentWallet!,
        tokens,
      })
      .then(() => {
        //更新本地缓存的asset config数据
        for (let token of tokens) {
          walletAssetConfig.push({
            tokenAddress: token.address,
            isShow: true,
          }); //新增
        }
        setBaseAssetConfig({
          ...baseAssetConfig,
          [currentWallet!]: [...walletAssetConfig],
        });
        //还要触发重新调用两次getWalletBalance接口（获取全部数据和isShow为true的数据）
        setAllParams({
          address: currentWallet!,
          tokenAddresses: [
            ...walletAssetConfig.map(item => item.tokenAddress),
          ],
        });
        setShowParams({
          address: currentWallet!,
          tokenAddresses: [
            ...walletAssetConfig
              .filter(item => item.isShow)
              .map(item => item.tokenAddress),
          ],
        });
      });
  };
  return {
    customAssetWithParams,
  };
};
